import { createElement, type HTMLAttributes } from 'react';

interface IconProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  /** Material Icons ligature name, e.g. "person_outline". */
  name: string;
  /** Angular's `fontSet="material-icons-outlined"`. */
  outlined?: boolean;
}

/**
 * Equivalent of `<mat-icon>`: a ligature glyph from the Google Material Icons font. It is rendered as a real
 * `<mat-icon>` element, like Angular, so the legacy SCSS that targets `span` (uppercase, margins, font sizes) or
 * `mat-icon` behaves exactly as before instead of hitting the icon and breaking its ligature. The class list is
 * the one `MatIcon` renders (`mat-ligature-font` only for the default font set).
 */
export function Icon({ name, outlined = false, className, ...rest }: IconProps) {
  const classes = [
    'mat-icon',
    'notranslate',
    outlined ? 'material-icons-outlined' : 'material-icons',
    !outlined && 'mat-ligature-font',
    'mat-icon-no-color',
    // a clickable icon shows the hand cursor without every caller having to add the class
    rest.onClick && 'cursor-pointer',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return createElement(
    'mat-icon',
    {
      className: classes,
      role: 'img',
      'aria-hidden': 'true',
      'data-mat-icon-type': 'font',
      // lets the global icon palette (styles/_icon-colors.scss) colour an icon by what it does
      'data-icon': name,
      ...(outlined ? { 'data-mat-icon-namespace': 'material-icons-outlined' } : {}),
      ...rest,
      // `mat-icon` is a custom element, and React writes a boolean `true` on one as an empty attribute. MUI's Tooltip marks its child with
      // `data-mui-internal-clone-element={true}` and reads it back as a string, so an empty value read as "props not forwarded".
      ...(rest['data-mui-internal-clone-element' as keyof typeof rest] ? { 'data-mui-internal-clone-element': 'true' } : {}),
    },
    name,
  );
}
